import Driver from 'driver.js';
import { IntlState } from 'react-intl-redux';
import { getDateDE } from '../../utilities/dateHelper';
import {
  ExtendedSurvey, SurveyHistoryDiff, TranslatedHistory, Survey,
} from '../../redux/types';

type SurveyHistoryDiffQuestions = SurveyHistoryDiff['sections'][0]['questions'];
type SurveyHistoryDiffCtxQuestions = SurveyHistoryDiff['sections'][0]['contextQuestions'];

const extractQuestionDiffFromDiffHistory = (
  questions: SurveyHistoryDiffQuestions,
): TranslatedHistory[] => {
  if (!questions) {
    return [];
  }
  const availQuests = questions.filter((q: SurveyHistoryDiffQuestions[0]) => (!!q.question));
  const hilightQuests = availQuests.reduce<TranslatedHistory[]>((acc, quest) => {
    const q = quest.question;
    if (q) {
      const newQuest = acc.concat(q);
      return newQuest;
    }
    return [];
  }, []);
  return hilightQuests;
};

const extractCtxQuestionDiffFromDiffHistory = (
  questions: SurveyHistoryDiffCtxQuestions,
): TranslatedHistory[] => {
  if (!questions) {
    return [];
  }
  const availQuests = questions.filter((q: SurveyHistoryDiffCtxQuestions[0]) => (!!q.question));
  const hilightQuests = availQuests.reduce<TranslatedHistory[]>((acc, quest) => {
    const q = quest.question;
    if (q) {
      const newQuest = acc.concat(q);
      return newQuest;
    }
    return [];
  }, []);
  return hilightQuests;
};

const extractOptionDifferenceFromDiffHistory = (
  questions: SurveyHistoryDiffQuestions,
): Partial<TranslatedHistory>[] => {
  const qWithOpts = questions.filter((q) => !!q.options);
  const hilightOpts = qWithOpts.reduce<Partial<TranslatedHistory>[]>((acc, qsWithOpts) => {
    const opts = qsWithOpts.options;
    if (opts) {
      const availOpts = opts.filter((o) => !!o.uuid);
      const newOpts = acc.concat(availOpts);
      return newOpts;
    }
    return [];
  }, []);
  return hilightOpts;
};

const extractCtxOptionDifferenceFromDiffHistory = (
  questions: SurveyHistoryDiffCtxQuestions,
): Partial<TranslatedHistory>[] => {
  const qWithOpts = questions.filter((q) => !!q.options);
  const hilightOpts = qWithOpts.reduce<Partial<TranslatedHistory>[]>((acc, qsWithOpts) => {
    const opts = qsWithOpts.options;
    if (opts) {
      const availOpts = opts.filter((o) => !!o.answer.uuid);
      const availMap = availOpts.map((optObj) => optObj.answer);
      const newOpts = acc.concat(availMap);
      return newOpts;
    }
    return [];
  }, []);
  return hilightOpts;
};

const getDiffHistory = (
  historyDiff: SurveyHistoryDiff, sectionIndex: number,
): Partial<TranslatedHistory>[] => {
  const section = historyDiff.sections[sectionIndex];
  const highlightQuests = extractQuestionDiffFromDiffHistory(section.questions);
  const highlightOpts = extractOptionDifferenceFromDiffHistory(section.questions);
  let highlightctxQuests = [] as TranslatedHistory[];
  let highlightCtxOpts = [] as Partial<TranslatedHistory>[];
  if (section.contextQuestions) {
    highlightctxQuests = extractCtxQuestionDiffFromDiffHistory(section.contextQuestions);
    highlightCtxOpts = extractCtxOptionDifferenceFromDiffHistory(section.contextQuestions);
  }
  const combinedDiffQuestions = highlightQuests.concat(highlightctxQuests);
  const partialTranslatedQuests = combinedDiffQuestions as Partial<TranslatedHistory>[];
  const hilightObjList = partialTranslatedQuests.concat(highlightOpts).concat(highlightCtxOpts);
  return hilightObjList;
};

/**
 * Step Array is compatible with driver.js
 * see driver.js documentation for details
 */
export const getHighlightStepArray = (
  historyDiff: SurveyHistoryDiff, sectionIndex: number, intl: IntlState, updatedAt?: string,
): Driver.Step[] => {
  const diffHistoryList = getDiffHistory(historyDiff, sectionIndex);
  const stepArray: Driver.Step[] = diffHistoryList.map((diffHistory) => {
    let description = diffHistory.de_old ? `<p>${diffHistory.de_old}</p>` : '';
    if (updatedAt) {
      description = description.concat(`<sub>${intl.messages['surveyHighlight.updatedAt']} ${getDateDE(updatedAt)}</sub>`);
    }
    return {
      element: `#highlight-${diffHistory.uuid}`,
      popover: {
        title: `<h5>${intl.messages['surveyHighlight.oldText']}</h5>`,
        description,
        nextBtnText: intl.messages.next,
        prevBtnText: intl.messages.back,
        closeBtnText: intl.messages.genericClose,
        doneBtnText: intl.messages.done,
      },
    };
  });
  return stepArray;
};

export const extendSurveyWithHighlightFlags = (
  survey: Survey,
  historyDiff: SurveyHistoryDiff,
): ExtendedSurvey => {
  const historySections = historyDiff.sections;
  const extendedSurvey: ExtendedSurvey = {
    ...survey,
    extended: true,
    payload: {
      ...survey.payload,
      sections: survey.payload.sections.map((section, secIndex) => ({
        ...section,
        questions: section.questions.map((question, questIndex) => ({
          ...question,
          question: {
            ...question.question,
            highlight: (
              historySections[secIndex].questions[questIndex].question
              && historySections[secIndex].questions[questIndex].question?.uuid
                ? true : undefined
            ),
          },
          options: question.options?.map((option, opIndex) => ({
            ...option,
            highlight: ((): boolean|undefined => {
              const historyOpts = historySections[secIndex].questions[questIndex].options;
              if (
                historyOpts !== undefined && historyOpts[opIndex] && historyOpts[opIndex].uuid
              ) {
                return true;
              }
              return undefined;
            })(),
          })),
        })),
        contextQuestions: section.contextQuestions.map((ctxQuestion, ctxQIndex) => ({
          ...ctxQuestion,
          question: {
            ...ctxQuestion.question,
            highlight: ((): boolean|undefined => {
              const historyCtxQuestions = historySections[secIndex].contextQuestions;
              if (historyCtxQuestions && historyCtxQuestions[ctxQIndex]
                && historyCtxQuestions[ctxQIndex].question
                && historyCtxQuestions[ctxQIndex].question?.uuid) {
                return true;
              }
              return undefined;
            })(),
          },
          options: ctxQuestion.options?.map((ctxOption, ctxOpIndex) => ({
            ...ctxOption,
            answer: {
              ...ctxOption.answer,
              highlight: ((): boolean|undefined => {
                const historyCtxQuestions = historySections[secIndex].contextQuestions;
                if (
                  historyCtxQuestions && historyCtxQuestions[ctxQIndex]
                  && historyCtxQuestions[ctxQIndex].options
                ) {
                  const historyCtxOptions = historyCtxQuestions[ctxQIndex].options;
                  if (historyCtxOptions && historyCtxOptions[ctxOpIndex]
                    && historyCtxOptions[ctxOpIndex].answer.uuid) {
                    return true;
                  }
                  return undefined;
                }
                return undefined;
              })(),
            },
          })),
        })),
      })),
    },
  };
  return extendedSurvey;
};
